import React from "react"
import styled from "styled-components"
import { up } from "styled-breakpoints"
import Img from "gatsby-image/withIEPolyfill"
import { gsap } from "gsap/all"
import TransitionLink from "gatsby-plugin-transition-link"

import { H1 } from "../../atomic/atoms/type/H1"
import { H3 } from "../../atomic/atoms/type/H3"
import { P } from "../../atomic/atoms/type/P"
import { COLORS, TYPE } from "../../../theme/styles/globals"
import CaseStudyIndicator from "../../atomic/organisms/CaseStudyIndicator"

export default class ContentfulBlockFocus extends React.Component {
  state = {
    selected: 0,
    project: "",
    slug: "",
  }

  componentDidMount() {
    this.open(0)
  }

  open(selected) {
    this.setState({ selected: selected })

    const { items } = this.props

    items.forEach((item, index) => {
      if (selected === index) {
        gsap.to(`#tab-item-${index}`, {
          height: "auto",
          duration: 0.25,
          ease: "sine.easeOut",
        })
        gsap.to(`#tab-image-${index}`, {
          autoAlpha: 1,
          duration: 0.25,
          ease: "sine.easeOut",
        })
        this.setState({
          project: items[selected].project.title,
          slug: items[selected].project.slug,
        })
      } else {
        gsap.to(`#tab-item-${index}`, {
          height: 0,
          duration: 0.25,
          ease: "sine.easeOut",
        })
        gsap.to(`#tab-image-${index}`, {
          autoAlpha: 0,
          duration: 0.25,
          ease: "sine.easeOut",
        })
      }
    })
  }

  render() {
    const { title, items, mode, transition } = this.props

    return (
      <Container mode={mode}>
        <MediaWrap
          to={`/projects/${this.state.slug}`}
          exit={transition.exit}
          entry={transition.entry}
        >
          <Indicator>
            <CaseStudyIndicator />
          </Indicator>
          <CaseStudyTitle>{this.state.project}</CaseStudyTitle>
          {items.map((item, i) => (
            <ImageWrap key={i} id={`tab-image-${i}`}>
              <Image fluid={item.project.portraitImage.fluid} />
            </ImageWrap>
          ))}
        </MediaWrap>
        <TextWrap>
          <Title>{title}</Title>
          <Tabs>
            {items.map((item, i) => (
              <TabItem key={i} onClick={() => this.open(i)}>
                <Cross open={this.state.selected === i} />
                <TabTitle>{item.title}</TabTitle>
                <TabDescriptionWrap id={`tab-item-${i}`}>
                  <TabDescription>{item.description}</TabDescription>
                  <MobileMediaWrap
                    to={`/projects/${item.project.slug}`}
                    exit={transition.exit}
                    entry={transition.entry}
                  >
                    <Indicator>
                      <CaseStudyIndicator />
                    </Indicator>
                    <ImageWrap>
                      <Image fluid={item.project.portraitImage.fluid} />
                    </ImageWrap>
                  </MobileMediaWrap>
                </TabDescriptionWrap>
              </TabItem>
            ))}
          </Tabs>
        </TextWrap>
      </Container>
    )
  }
}

const Cross = ({ open }) => (
  <CrossWrap>
    {!open && (
      <svg
        x={0}
        y={0}
        viewBox="0 0 15 16"
        xmlSpace="preserve"
      >
        <path d="M0 6h15v3H0z" fill={"#191616"} />
        <path
          transform="rotate(90 7.5 8)"
          d="M-.5 6.5h16v3h-16z"
          fill={"#191616"}
        />
      </svg>
    )}
    {open && (
      <svg
        x={0}
        y={0}
        viewBox="0 0 15 16"
        xmlSpace="preserve"
      >
        <path d="M0 6h15v3H0z" fill={"#191616"} />
      </svg>
    )}
  </CrossWrap>
)

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  ${props =>
    props.mode === "LIGHT"
      ? `color:${COLORS.LIGHT_GREY_2};`
      : `color:${COLORS.LIGHT_GREY_4};`};
  padding-left: 20px;
  padding-right: 20px;

  ${up("md")} {
    align-items: center;
    justify-content: center;
    flex-direction: row;
    padding-left: 0px;
    padding-right: 0px;
  }
`

const MediaWrap = styled(TransitionLink)`
  display: none;

  ${up("md")} {
    display: block;
    height: 70vh;
    width: 50vh;
    position: relative;
    background-color: black;
    margin-right: 100px;
  }
`

const MobileMediaWrap = styled(TransitionLink)`
  display: block;
  height: 320px;
  width: 100%;
  position: relative;
  background-color: black;

  ${up("md")} {
    display: none;
  }
`
const Title = styled(H1)`
  color: ${COLORS.LIGHT_GREY_1};
  margin-bottom: 50px;

  ${up("md")} {
    position: absolute;
    font-size: 174px;
    top: 50%;
    margin-top: -400px;
    margin-bottom: 0px;
  }
`

const TextWrap = styled.div`
  display: flex;
  flex-direction: column;
`

const ImageWrap = styled.div`
  display: block;
  height: 100%;
  width: 100%;
  position: absolute;
  overflow: hidden;
  background-color:black;

`

const Image = styled(Img)`
  top: 0px;
  left: 0px;
  width: 100%;
  height: 100%;
  transition: all 0.2s ease-in-out;
  opacity:.7;

  &:hover {
    transform: scale(1.1);
    opacity:.5;
  }
`

const Tabs = styled.div`
  ${up("md")} {
    width: 650px;
    margin-left: 140px;
    padding-right: 140px;
  }
`

const TabItem = styled.div`
  margin-bottom: 26px;
  border-bottom: 1px solid ${COLORS.DARK_GREY_3};
  position: relative;
  cursor: pointer;

  &:last-child {
    border-bottom: none;
  }
`

const CrossWrap = styled.div`
  position: absolute;
  right: 0px;
  top: 0px;
  width: 16px;
  height: 16px;
  -webkit-font-smoothing: antialiased;
  transform: translate3d(0, 0, 0);

  ${up("md")} {
    top: 8px;
  }
`

const TabTitle = styled(H3)`
  padding-bottom: 26px;
  font-size: 28px;

  ${up("md")} {
    font-size: 32px;
  }
`

const TabDescriptionWrap = styled.div`
  height: 0px;
  overflow: hidden;
`

const TabDescription = styled(P)`
  padding-bottom: 26px;
`

const Indicator = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  pointer-events: none;
`

const CaseStudyTitle = styled.h3`
  position: absolute;
  bottom: 8vh;
  left: -6vh;
  color: white;
  z-index: 10;
  font-size:6vh;
  line-height:6vh;
  font-family:${TYPE.SERIF_BOLD_FONT};
  font-weight:normal;
  pointer-events:none;
  width:80%;
`
